/**
 * Крупный просмотр варианта справа (прежний `#preview` + editor.js).
 *
 * Генерации смотрят ПОДРЯД: щёлкнул, оценил, стрелкой к следующей — вкладка на
 * каждый кадр превратила бы разбор десяти вариантов в десять вкладок.
 *
 * Панель не держит своей копии генерации: она читает ту же ленту из кэша,
 * что и плитки, и звезду/пересъёмку жмёт теми же действиями. Поэтому после
 * опроса (каждые 3 с) она не закрывается и не расходится с лентой ни ценой,
 * ни звездой.
 */

import clsx from "clsx";
import { ArrowUpFromLine, ChevronLeft, ChevronRight, ExternalLink, Star, X } from "lucide-react";
import { useEffect } from "react";

import { mediaUrl, when } from "@/lib/media";
import { useGenNote, useGens, usd, useVariantActions, viewable } from "@/lib/project";

import { ErrorLine } from "./ui";

export type PreviewTarget = { sid: number; vid: number; where: string };

export function GenPreview({
  pid,
  target,
  onChange,
}: {
  pid: number;
  target: PreviewTarget;
  onChange: (next: PreviewTarget | null) => void;
}) {
  const { data } = useGens(pid, target.sid);
  const note = useGenNote(pid, target.sid);
  const actions = useVariantActions(pid, target.sid);
  const list = viewable(data?.gens ?? []);
  const at = list.findIndex((g) => g.variant_id === target.vid);
  const tile = at >= 0 ? list[at] : undefined;

  // Вариант пропал из ленты — показывать нечего, и панель не должна врать.
  useEffect(() => {
    if (data && !tile) onChange(null);
  }, [data, tile, onChange]);

  const step = (delta: number) => {
    if (at < 0 || list.length < 2) return;
    const next = list[(at + delta + list.length) % list.length];
    if (next?.variant_id != null) onChange({ ...target, vid: next.variant_id });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable]");
      if (e.key === "Escape") onChange(null);
      else if (!typing && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
        step(e.key === "ArrowRight" ? 1 : -1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  if (!tile || tile.variant_id === null) return null;
  const vid = tile.variant_id;
  const picked = tile.pick === "picked";

  return (
    <aside
      data-annot="крупный просмотр"
      className="fixed top-[var(--spacing-topbar)] right-0 bottom-0 z-40 flex w-[min(440px,100vw)] flex-col gap-2.5 border-l border-border bg-bg-subtle p-3 pb-16 shadow-2xl"
    >
      <div className="flex items-baseline gap-2">
        <div className="min-w-0 flex-1 truncate text-[12.5px] text-fg-muted">
          {target.where}
          {list.length ? ` · ${at + 1} из ${list.length}` : ""}
        </div>
        <button
          type="button"
          title="закрыть (Esc)"
          onClick={() => onChange(null)}
          className="grid size-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <X size={15} />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center rounded-[var(--radius-ui)] bg-bg">
        {list.length > 1 ? (
          <button
            type="button"
            title="предыдущий (←)"
            onClick={() => step(-1)}
            className="absolute left-0 z-10 grid h-14 w-8 place-items-center rounded-r-[var(--radius-ui)] bg-bg/70 text-fg-muted hover:text-accent"
          >
            <ChevronLeft size={18} />
          </button>
        ) : null}
        <img
          src={mediaUrl(tile.asset_path)}
          alt="выбранный кадр"
          className="block max-h-full max-w-full object-contain"
        />
        {list.length > 1 ? (
          <button
            type="button"
            title="следующий (→)"
            onClick={() => step(1)}
            className="absolute right-0 z-10 grid h-14 w-8 place-items-center rounded-l-[var(--radius-ui)] bg-bg/70 text-fg-muted hover:text-accent"
          >
            <ChevronRight size={18} />
          </button>
        ) : null}
      </div>

      <div className="tnum text-[11.5px] text-fg-faint">
        {[
          `${tile.generation_model} · ${tile.quality} · ${tile.size}`,
          tile.cost_each !== null ? usd(tile.cost_each) : "",
          when(tile.date_create),
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>

      <ErrorLine text={note} />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={actions.picking === vid}
          onClick={() => actions.pick(vid)}
          className={clsx(
            "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border px-3 text-[12.5px] disabled:opacity-50",
            picked
              ? "border-accent bg-accent-dim text-accent-text"
              : "border-border bg-bg hover:border-accent hover:bg-bg-hover",
          )}
        >
          <Star size={13} fill={picked ? "currentColor" : "none"} />
          {picked ? "снять отметку" : "отметить"}
        </button>
        <button
          type="button"
          title="переснять этот кадр в качестве 1088×1920"
          disabled={actions.reshooting === vid}
          onClick={() => actions.reshoot(vid)}
          className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg px-3 text-[12.5px] hover:border-accent hover:bg-bg-hover disabled:opacity-50"
        >
          <ArrowUpFromLine size={13} />
          переснять
        </button>
        <a
          href={mediaUrl(tile.asset_path)}
          target="_blank"
          rel="noopener"
          className="ml-auto inline-flex items-center gap-1 text-[12.5px] text-accent-text hover:underline"
        >
          оригинал <ExternalLink size={12} />
        </a>
      </div>
    </aside>
  );
}
